// Copyright 2024 and onwards, All rights reserved.
// Author: Hariharan Kolam

import * as React from 'react';
import { md5 } from '@mui/x-license-pro/encoding/md5';
import { LicenseInfo } from '@mui/x-license-pro';
import { LICENSE_SCOPES } from '@mui/x-license-pro/utils/licenseScope';
import { LICENSING_MODELS } from '@mui/x-license-pro/utils/licensingModel';
import { fetchCompanyRows, fetchCompaniesById, fetchIcpById, fetchQueries, runMatchCount, listAllDynamicColumns, fetchIcpsById } from './Apis';
import { Button, Stack, Box, Typography } from '@mui/material';
import _ from 'lodash';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import FunctionsIcon from '@mui/icons-material/Functions';
import { getCompLinkedInId } from './common/utils/getCompanyLinkedInId';
import TextField from '@mui/material/TextField';

import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from '@mui/x-data-grid-pro';
import { colors } from './common/constants';
const companyDefaultLogo = "data:image/png;base64, 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"

export var CidMatchCountCache = {};
export default function LicenseInit() {
  let orderNumber = '';
  let expiryTimestamp = Date.now();
  let scope = LICENSE_SCOPES[1];
  let licensingModel = LICENSING_MODELS[0];
  let licenseInfo = `O=${orderNumber},E=${expiryTimestamp},S=${scope},LM=${licensingModel},KV=2`;
  LicenseInfo.setLicenseKey(md5(btoa(licenseInfo)) + btoa(licenseInfo));

  fetchQueries();
  fetchIcpsById();
}

export const imgOnError = (ev) => {
  const trigger = ev.target;
  trigger.src = companyDefaultLogo
}

export function debounce(fn, delay) {
  let timeoutId;
  return function (...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
}

export const myLocaleText = {
  // Provide translations for the filter operator labels
  headerFilterOperatorContains: 'contains',
  headerFilterOperatorEquals: 'equals',
  headerFilterOperatorEqual: 'equals',
  headerFilterOperatorStartsWith: 'starts with',
  headerFilterOperatorEndsWith: 'ends with',
  headerFilterOperatorIs: 'is',
  headerFilterOperatorNot: 'is not',
  headerFilterOperatorAfter: 'is after',
  headerFilterOperatorOnOrAfter: 'is on or after',
  headerFilterOperatorBefore: 'is before',
  headerFilterOperatorOnOrBefore: 'is on or before',
  headerFilterOperatorIsEmpty: 'is empty',
  headerFilterOperatorIsNotEmpty: 'is not empty',
  headerFilterOperatorGreaterThan: 'greater than', // Custom label for 'greater than'
  headerFilterOperatorGreaterThanOrEqual: 'greater than or equal',
  headerFilterOperatorLessThan: 'less than', // Custom label for 'less than'
  headerFilterOperatorLessThanOrEqual: 'less than or equal',
};

export const fetchRows = async ({ fromIndex, toIndex, sortModel, filterModel, searchTerm, selectedCompanies }) => {
  console.log("fetchRows: ", fromIndex, toIndex, sortModel, filterModel, searchTerm, selectedCompanies);
  let data = await fetchCompanyRows({ fromIndex, toIndex, sortModel, filterModel, searchTerm });
  const hasMoreData = data.data.length === (toIndex - fromIndex);

  let ids = selectedCompanies.map((it) => it.id);
  let rows = [];
  data.data.forEach((it, idx) => {
    if (ids.indexOf(it.meta_ref) >= 0) {
      return;
    }
    let competitors = [];
    if (it.competitors) {
      it.competitors.forEach((c) => {
        competitors.push(c.name);
      })
    }
    let prodcats = [];
    if (it.products) {
      it.products.forEach((p) => {
        let p2 = p.product_categories ? p.product_categories : []
        prodcats = prodcats.concat(p2);
      })
    }
    let prodcomps = [];
    if (it.products) {
      it.products.forEach((p) => {
        let p2 = p.product_competitors ? p.product_competitors : []
        let p33 = p2.map((t) => t.name);
        prodcomps = prodcomps.concat(p33);
      })
    }
    let parentcomps = [];
    if (it.parent_companies) {
      it.parent_companies.forEach((p) => {
        let p2 = p.parent_companies ? p.parent_companies : [];
        let p33 = p2.map((t) => t.company_name);
        parentcomps = parentcomps.concat(p33);
      })
    }

    rows.push({
      id: it.meta_ref,
      company_name: { url: it.domain, name: it.company_name },
      description: it.description,
      last_known_state: it.last_known_state,
      "pb_data.ownership_status": it?.pb_data?.ownership_status,
      "pb_data.acquirer": it?.pb_data?.acquirer,
      "pb_data.financing_status": it?.pb_data?.financing_status,
      "pb_data.org_type_status": it?.pb_data?.pb_data?.org_type_status,
      "competitors.name": competitors,
      "products.product_categories": prodcats,
      "products.product_competitors": prodcomps,
      "parent_companies.company_name": parentcomps,
      'industry.crunchbase_category_groups': it.industry?.crunchbase_category_groups,
      'industry.sub_categories': it.industry?.sub_categories,
      'curr_financial_info.est_revenue': it.curr_financial_info?.est_revenue,
      comp_size: it?.comp_size,
      org_type: it?.org_type,
      website: it?.website,
      biz_models: it?.biz_models,
      "hq_location.state": it?.hq_location?.state,
      "hq_location.city": it?.hq_location?.city,
      "hq_location.country": it?.hq_location?.country,
      "funding": it?.funding,
      linkedIn_id: getCompLinkedInId(it),
    });
  })
  return { rows: rows, hasMoreData: hasMoreData };
};

export function basename(path) {
  return path.split('/').reverse()[0];
}

export const fetchRowsByIds = async (ids) => {
  let data = await fetchCompaniesById(ids);
  if (data.length === 0) {
    return [];
  }
  let rows = data.data.map((it, idx) => {
    let competitors = [];
    if (it.competitors) {
      it.competitors.forEach((c) => {
        competitors.push(c.name);
      })
    }
    let prodcats = [];
    if (it.products) {
      it.products.forEach((p) => {
        let p2 = p.product_categories ? p.product_categories : []
        prodcats = prodcats.concat(p2);
      })
    }

    let prodcomps = [];
    if (it.products) {
      it.products.forEach((p) => {
        let p2 = p.product_competitors ? p.product_competitors : [];
        let p33 = p2.map((t) => t.name);
        prodcomps = prodcomps.concat(p33);
      })
    }

    let parentcomps = [];
    if (it.parent_companies) {
      it.parent_companies.forEach((p) => {
        let p2 = p.parent_companies ? p.parent_companies : [];
        let p33 = p2.map((t) => t.company_name);
        parentcomps = parentcomps.concat(p33);
      })
    }
    return {
      id: it.meta_ref,
      company_name: { url: it.domain, name: it.company_name },
      description: it.description,
      last_known_state: it.last_known_state,
      "pb_data.ownership_status": it?.pb_data?.ownership_status,
      "pb_data.acquirer": it?.pb_data?.acquirer,
      "pb_data.financing_status": it?.pb_data?.financing_status,
      "pb_data.org_type_status": it?.pb_data?.pb_data?.org_type_status,
      "competitors.name": competitors,
      "products.product_categories": prodcats,
      "products.product_competitors": prodcomps,
      "parent_companies.company_name": parentcomps,
      'industry.crunchbase_category_groups': it.industry?.crunchbase_category_groups,
      'industry.sub_categories': it.industry?.sub_categories,
      'curr_financial_info.est_revenue': it.curr_financial_info?.est_revenue,
      comp_size: it?.comp_size,
      org_type: it?.org_type,
      website: it?.website,
      biz_models: it?.biz_models,
      "hq_location.state": it?.hq_location?.state,
      "hq_location.country": it?.hq_location?.country,
      "funding": it?.funding,
      linkedIn_id: getCompLinkedInId(it),
    };
  })
  return rows;
};

export function CustomToolbar(props) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Stack 
        direction="row" 
        spacing={1} 
        alignItems="center"
        sx={{ 
          justifyContent: 'space-between', 
          width: '99%', 
          padding: '8px',
          "& .MuiButton-colorPrimary": {
            color: colors.white,
            backgroundColor: colors['blue-300'],
            height: '36px',
            padding: '4px 8px',
            "&:hover":{
              backgroundColor: colors["blue-400"],
            },
          },
          "& .MuiBadge-badge":{
            backgroundColor: colors['grey-200'],
            color: colors['blue-400'],
          }
        }}
      >
        <div style={{
          display: 'flex',
          gap: 8
        }}>
          <GridToolbarFilterButton sx={{
                  height: '36px',
                }} />
          <GridToolbarColumnsButton sx={{
                  height: '36px',
                }}/>
        </div> 
        
          {props.show_dc ? <div>
            <TextField
              id="outlined-basic"
              label="Search"
              variant="outlined"
              size="small"
              onChange={(e) => props.onSearch(e.target.value)}
              sx={{
                marginRight: '8px',
              }}
            />
            <Tooltip title="Create aggregate columns for each company that show the total number of titles, skills, or the total count of matches with Findem attributes.">
              <Button
                sx={{
                  height: '36px',
                  padding: '4px 8px',
                }}
                color="primary"
                size="small"
                id="basic-button"
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                startIcon={<FunctionsIcon sx={{ mr: -0.5 }} />}
              >
                Talent Breakdown
              </Button>
            </Tooltip>
            <Menu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{ 'aria-labelledby': 'basic-button' }}
            >
              <Tooltip title="Show count of employees with specific Titles" placement="right">
                <MenuItem onClick={() => { handleClose(); props.customCol("title") }}>Titles</MenuItem>
              </Tooltip>
              <Tooltip title="Show count of employees with specific Skills" placement="right">
                <MenuItem onClick={() => { handleClose(); props.customCol("skills") }}>Skills</MenuItem>
              </Tooltip>
              <Tooltip title="Show count of employees with specific Findem Attributes" placement="right">
                <MenuItem onClick={() => { handleClose(); props.customCol("attrs") }}>Findem Attributes</MenuItem>
              </Tooltip>
              <Tooltip title="Show count of employees matching this Findem Search" placement="right">
                <MenuItem onClick={() => { handleClose(); props.customCol("search") }}>Findem Search</MenuItem>
              </Tooltip>
            </Menu>
          </div> : ""}
        {props.show_export ? <GridToolbarExport /> : null}
      </Stack>
    </GridToolbarContainer>
  );
}

export const CustomNoRowsOverlay = (props) => {
  return (<Box
    sx={{
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'background.paper', // Or any other color that fits your theme
    }}
  >
    <Typography variant="subtitle1" component="div" color="text.secondary">
      {props.message}
    </Typography>
  </Box>)
}

export const generateTimestampedName = (prefix) => {
  const timestamp = new Date().toISOString().replace(/[-:.TZ]/g, '');
  return prefix + timestamp;
}

export function deepEqual(object1, object2) {
  if (_.isEqual(object1, object2)) {
    return true;
  }
  return false;
}

function timeAgo(created_at) {
  const createdAtDate = new Date(created_at);
  const currentDate = new Date();

  const differenceInTime = currentDate - createdAtDate; // Difference in milliseconds
  const minutes = Math.floor(differenceInTime / (1000 * 60));
  const hours = Math.floor(differenceInTime / (1000 * 3600));
  const days = Math.floor(differenceInTime / (1000 * 3600 * 24));

  if (days > 0) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } else if (hours > 0) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (minutes > 0) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  } else {
    return "Just now";
  }
}

export function displayTimeAgo(created_at) {
  return timeAgo(created_at);
}

export async function runDynColMatchCount(cid, query) {
  let bool_str = 'workexp.current.company.id:' + cid;
  let reqs = [{
    bool_str: '( ' + bool_str + ' )',
    section_id: "company_names",
    section_name: "Current Companies",
    section_order: 3,
    section_type: "Must Have"
  }];
  let target = [{
    section_id: 'advanced',
    section_name: 'Advanced',
    section_type: 'Nice to have',
    // @ts-ignore
    bool_str: '( ' + query.join('OR') + ' )'
  }];
  let res = await runMatchCount(reqs, target);
  return res;
}

const cacheLookup = (cid, q_str) => {
  if (!CidMatchCountCache[q_str]) {
    CidMatchCountCache[q_str] = [];
  }
  let out = null;
  CidMatchCountCache[q_str].forEach((res) => {
    if (res.company_id === cid) {
      out = res;
    }
  });
  return out;
}

export async function runDynColMatchCount2(cids, query) {
  let target = [];
  let reqs = [];
  let final_res = {};
  let q_str = '';
  let unknown = [];
  if ((query.length == 1) && fetchIcpById(query[0])) {
    // check if its an ICP
    let icp = fetchIcpById(query[0]);
    reqs = icp.requirements ? icp.requirements : [];
  } else {
    reqs = [{
      section_id: 'advanced',
      section_name: 'Advanced',
      section_type: "Must Have",
      // @ts-ignore
      bool_str: '(' + query.join(' OR ') + ')'
    }];  
  }
  
  q_str = query.join(' OR ');

  cids.forEach((cid) => {
    let res = cacheLookup(cid, q_str);
    if (res) {
      final_res[res.company_id] = res.match_count;
    } else {
      unknown.push(cid);
    }
  });
  if (unknown.length === 0) {
    return final_res;
  }

  unknown.forEach((cid) => {
    let bool_str = 'workexp.current.company.id:' + cid;
    target.push({
      bool_str: '(' + bool_str + ')',
      section_id: "company_names",
      section_name: "Current Companies",
      section_order: 3,
      section_type: 'Nice to have'
    });
  });

  let res = await runMatchCount(reqs, target);
  let autoid_cid_map = {};
  if (!res.target_requirements) {
    res.target_requirements = []
  }
  res.target_requirements.forEach((t) => {
    let cid = t.bool_str.split(':')[1].split(')')[0];
    let macro_id = t.macro_id;
    autoid_cid_map[macro_id] = cid;
  });
  if (!res.count_by_queries) {
    res.count_by_queries = []
  }

  Object.keys(res.count_by_queries).forEach((key) => {
    let count = res.count_by_queries[key];
    let cid = autoid_cid_map[key];
    CidMatchCountCache[q_str].push({ company_id: cid, match: count });
    final_res[cid] = count;
  });
  return final_res;
}

// export enum IDynColmnType {
//   Title = 0,
//   Company = 1,
//   Attrs = 2,
//   Search = 3,
// }
export async function loadDynCol(type) {
  if (type == 0) {
    return await listTitleDynamicColumns();
  } else if (type == 1) {
    return await listSkillDynamicColumns();
  } else if (type == 2) {
    return await listAttrsDynamicColumns();
  } else if (type == 3) {
    return await listSearchDynamicColumns();
  }
}

async function listTitleDynamicColumns() {
  let data = await listAllDynamicColumns();
  let title_cols = [];
  data.forEach((d) => {
    if (d.type == 0) {
      title_cols.push(d);
    }
  });
  return title_cols;
}

async function listSearchDynamicColumns() {
  let data = await listAllDynamicColumns();
  let search_cols = [];
  data.forEach((d) => {
    if (d.type == 3) {
      search_cols.push(d);
    }
  });
  return search_cols;
}

async function listSkillDynamicColumns() {
  let data = await listAllDynamicColumns();
  let skill_cols = [];
  data.forEach((d) => {
    if (d.type == 1) {
      skill_cols.push(d);
    }
  });
  return skill_cols;
}

export async function listAttrsDynamicColumns() {
  let data = await listAllDynamicColumns();
  let attrs_cols = [];
  data.forEach((d) => {
    if (d.type == 2) {
      attrs_cols.push(d);
    }
  });
  return attrs_cols;
}

export const sortOptionsByInputMatchIndex = (inputValue, options) => {
  const inputValueLower = inputValue.toLowerCase();
  return options.sort((a, b) => {
    const aIndex = (a.label || a.title || '').toLowerCase().indexOf(inputValueLower);
    const bIndex = (b.label || b.title || '').toLowerCase().indexOf(inputValueLower);
    return aIndex - bIndex;
  });
};

export const BIZMODELS = [
  'Advertising',
  'Affiliate',
  'Aircraft Management',
  'Aircraft Sales',
  'Assisted Living',
  'B2B',
  'B2B & B2B',
  'B2B.B2C',
  'B2B2C',
  'B2B2G',
  'B2C',
  'B2C.',
  'B3B',
  'Banking',
  'Brick and Mortar',
  'C2C',
  'Catering',
  'Charter',
  'Classifieds',
  'Completions',
  'Consulting',
  'Contracting',
  'Defence',
  'Direct Sales',
  'Distressed Debt',
  'Donations',
  'Drive-Through',
  'Education',
  'Energy Storage',
  'Engineering',
  'Events',
  'FBO',
  'Fleet Outsourcing',
  'Franchise',
  'Freemium',
  'Gathering',
  'General Contractor',
  'Independent Living',
  'Integrated Transportation',
  'Investment Sales',
  'Investments',
  'Last Mile Services',
  'Lead Generation',
  'Licensing',
  'Liscensing',
  'Loan Servicing',
  'Loans',
  'Logistics',
  'Maintenance',
  'MarketPlace',
  'Marketing',
  'Marketplace',
  'Memory Care',
  'Metamediary',
  'Mortgage',
  'Mortgage Banking',
  'Natural gas',
  'On-Demand',
  'On-Demand Brick and Mortar',
  'Operations and Maintenance',
  'Outpatient Rehabilitation',
  'Outreach',
  'Outsourced Services',
  'Patient Care',
  'Private Equity',
  'Processing',
  'Procurement Construction',
  'Project Development Support',
  'Recruitment',
  'Rent to Own',
  'Repowering',
  'Research',
  'Respite Care Short Term Stays',
  'SaaS',
  'Staffing',
  'Subsciption',
  'Subscription',
  'Technology',
  'Training',
  'Transportation Management',
  'Volunteering',
  'Wholesale',
  'Workforce Solutions',
  'marketplace'
];

export const LKS = [
  'Exited (Acquired)', 'Exited (IPO)',
  'Grant', 'Late', 'Private Equity',
  'Seed', 'Series A', 'Series A-1',
  'Series B', 'Series B-1', 'Series C',
  'Series D', 'Series E', 'Series F',
  'Unattributed', 'Unknown', 'a',
  'acquired', 'acquisition', 'angel',
  'b', 'c', 'convertible note',
  'corporate round', 'crowdfunding', 'd',
  'debt', 'debt financing', 'e',
  'equity crowdfunding', 'exited (acquired)', 'exited (ipo)',
  'f', 'g', 'grant',
  'h', 'initial coin offering', 'ipo',
  'late', 'non equity assistance', 'post ipo debt',
  'post ipo equity', 'pre seed', 'private equity',
  'product crowdfunding', 'secondary market', 'seed',
  'series a', 'series a-1', 'series a-2',
  'series b', 'series b-1', 'series c',
  'series c-1', 'series d', 'series d-1',
  'series e', 'series f', 'series g',
  'series h', 'series i', 'series j',
  'series unknown', 'unattribut', 'unattributed',
  'undisclosed', 'unknown', 'venture'
];

export const COUNTRIES = [
  "ABW", "AD", "AE", "AF", "AFG", "AG", "AGO", "AI", "AIA", "AL", "ALA", "ALB",
  "AM", "AND", "AO", "AR", "ARE", "ARG", "ARM", "ASM", "AT", "ATG", "AU", "AUS",
  "AUT", "AW", "AX", "AZ", "AZE", "Afghanistan", "Albania", "Algeria", "Andorra",
  "Angola", "Anguilla", "Antigua", "Argentina", "Armenia", "Aruba", "Australia",
  "Austria", "Azerbaijan", "BA", "BAH", "BB", "BD", "BDI", "BE", "BEL", "BEN", "BF",
  "BFA", "BG", "BGD", "BGR", "BH", "BHR", "BIH", "BJ", "BLM", "BLR", "BLZ", "BM",
  "BMU", "BN", "BO", "BOL", "BR", "BRA", "BRB", "BRN", "BS", "BT", "BTN", "BVT", "BW",
  "BWA", "BY", "BZ", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus",
  "Belgium", "Belize", "Benin", "Bermuda", "Bhutan", "Bolivia", "Bosnia and Herzegovina",
  "Botswana", "Brazil", "British Indian Ocean Territory", "British Virgin Islands",
  "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "CA", "CAF", "CAN", "CCK", "CD",
  "CG", "CH", "CHE", "CHL", "CHN", "CI", "CIV", "CK", "CL", "CM", "CMR", "CN", "CO",
  "COD", "COG", "COK", "COL", "COM", "CPV", "CR", "CRI", "CU", "CUB", "CV", "CW", "CY",
  "CYM", "CYP", "CZ", "CZE", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Cayman Islands", "Central African Republic", "Chad", "Chile", "China", "Colombia",
  "Comoros", "Congo", "Cook Islands", "Costa Rica", "Croatia", "Cuba", "Curacao",
  "Cyprus", "Czech Republic", "DE", "DEU", "DJI", "DK", "DM", "DMA", "DNK", "DO",
  "DOM", "DZ", "DZA", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "EC",
  "ECU", "EE", "EG", "EGY", "ERI", "ES", "ESH", "ESP", "EST", "ET", "ETH", "East Timor",
  "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia",
  "Ethiopia", "FI", "FIN", "FJ", "FJI", "FLK", "FO", "FR", "FRA", "FRO", "FSM",
  "Falkland Islands", "Faroe Islands", "Fiji", "Finland", "France", "French Polynesia",
  "GA", "GAB", "GB", "GBR", "GE", "GEO", "GG", "GGY", "GH", "GHA", "GI", "GIB", "GIN",
  "GL", "GLP", "GM", "GMB", "GN", "GNB", "GNQ", "GP", "GQ", "GR", "GRC", "GRD", "GRL",
  "GT", "GTM", "GU", "GUF", "GUM", "GUY", "GY", "Gabon", "Georgia", "Germany", "Ghana",
  "Gibraltar", "Greece", "Greenland", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau",
  "Guyana", "HK", "HKG", "HN", "HND", "HR", "HRV", "HT", "HTI", "HU", "HUN", "Haiti",
  "Honduras", "Hong Kong", "Hungary", "ID", "IDN", "IE", "IL", "IM", "IMN", "IN", "IND",
  "IOT", "IQ", "IR", "IRL", "IRN", "IRQ", "IS", "ISL", "ISR", "IT", "ITA", "Iceland",
  "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Ivory Coast",
  "JAM", "JE", "JEY", "JM", "JO", "JOR", "JP", "JPN", "Jamaica", "Japan", "Jersey",
  "Jordan", "KAZ", "KE", "KEN", "KG", "KGZ", "KH", "KHM", "KN", "KNA", "KOR", "KR", "KW",
  "KWT", "KY", "KZ", "Kazakhstan", "Kenya", "Korea (Republic of)", "Kosovo", "Kuwait",
  "Kyrgyzstan", "LA", "LAO", "LB", "LBN", "LBR", "LBY", "LC", "LCA", "LI", "LIE", "LK",
  "LKA", "LR", "LS", "LSO", "LT", "LTU", "LU", "LUX", "LV", "LVA", "LY", "Laos", "Latvia",
  "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
  "MA", "MAC", "MAF", "MAR", "MC", "MD", "MDA", "MDG", "MDV", "ME", "MEX", "MF", "MG",
  "MHL", "MK", "MKD", "ML", "MLI", "MLT", "MM", "MMR", "MN", "MNE", "MNG", "MNP", "MO",
  "MOZ", "MP", "MQ", "MR", "MRT", "MSR", "MT", "MTQ", "MU", "MUS", "MV", "MW", "MWI",
  "MX", "MY", "MYS", "MYT", "MZ", "Macau", "Madagascar", "Malawi", "Malaysia", "Maldives",
  "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia",
  "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "NA",
  "NAM", "NC", "NCL", "NER", "NFK", "NG", "NGA", "NI", "NIC", "NL", "NLD", "NO", "NOR",
  "NP", "NPL", "NZ", "NZL", "Namibia", "Nepal", "Netherlands", "New Caledonia", "New Zealand",
  "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Northern Mariana Islands",
  "Norway", "OM", "OMN", "Oman", "PA", "PAK", "PAN", "PE", "PER", "PF", "PG", "PH", "PHL",
  "PK", "PL", "PM", "PNG", "POL", "PR", "PRI", "PRK", "PRT", "PRY", "PSE", "PT", "PY", "PYF",
  "Pakistan", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines",
  "Poland", "Portugal", "QA", "QAT", "Qatar", "RE", "REU", "RO", "ROM", "RS", "RU", "RUS",
  "RW", "RWA", "Reunion", "Romania", "Russia", "Rwanda", "SA", "SAU", "SB", "SC", "SD", "SDN",
  "SE", "SEN", "SG", "SGP", "SGS", "SI", "SJM", "SK", "SLB", "SLE", "SLV", "SM", "SMR", "SN",
  "SO", "SOM", "SPM", "SR", "SRB", "SS", "ST", "STP", "SUR", "SV", "SVK", "SVN", "SWE", "SWZ",
  "SX", "SY", "SYC", "SYR", "SZ", "Saint Helena", "Saint Kitts and Nevis", "Saint Lucia",
  "Saint Vincent", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal",
  "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Sint Maarten", "Slovakia", "Slovenia",
  "Solomon Islands", "Somalia", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sudan",
  "Suriname", "Swaziland", "Sweden", "Switzerland", "Syria", "TAN", "TC", "TCA", "TCD", "TG",
  "TGO", "TH", "THA", "TJ", "TJK", "TKM", "TLS", "TM", "TN", "TO", "TON", "TR", "TT", "TTO",
  "TUN", "TUR", "TUV", "TW", "TWN", "TZ", "Taiwan", "Taiwan, Province of China", "Tajikistan",
  "Tanzania", "Thailand", "The Gambia", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey",
  "Turkmenistan", "Turks and Caicos", "UA", "UG", "UGA", "UKR", "UMI", "URY", "US", "USA", "UY",
  "UZ", "UZB", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "VC", "VE", "VEN", "VG", "VI", "VN", "VNM", "VU", "Vanuatu", "Venezuela",
  "Viet Nam", "Vietnam", "WS", "West Bank", "YE", "YEM", "YT", "Yemen", "ZA", "ZAF", "ZM", "ZMB",
  "ZW", "ZWE", "Zambia", "Zimbabwe", "abw", "ad", "ae", "af", "afg", "ag", "ago", "ai", "aia", "al",
  "ala", "alb", "am", "an", "and", "ao", "aq", "ar", "are", "arg", "arm", "as", "asm", "at", "ata",
  "atg", "au", "aus", "aut", "aw", "ax", "az", "aze", "ba", "bb", "bd", "bdi", "be", "bel", "ben",
  "bf", "bfa", "bg", "bgd", "bgr", "bh", "bhr", "bhs", "bi", "bih", "bj", "bl", "blr", "blz", "bm",
  "bmu", "bn", "bo", "bol", "bq", "br", "bra", "brb", "brn", "bs", "bt", "btn", "bv", "bw", "bwa",
  "by", "bz", "ca", "caf", "can", "cb", "cc", "cck", "cd", "cf", "cg", "ch", "che", "chl", "chn",
  "ci", "civ", "ck", "cl", "cm", "cmr", "cn", "co", "cod", "cog", "cok", "col", "com", "cr", "cri",
  "cs", "cu", "cub", "cuw", "cv", "cw", "cx", "cxr", "cy", "cym", "cyp", "cz", "cze", "de", "deu",
  "dj", "dji", "dk", "dm", "dma", "dnk", "do", "dom", "dz", "dza", "ec", "ecu", "ee", "eg", "egy",
  "eh", "en", "er", "eri", "es", "esh", "esp", "est", "et", "eth", "fi", "fin", "fj", "fji", "fk",
  "fm", "fo", "fr", "fra", "fro", "fsm", "ga", "gab", "gb", "gbr", "gd", "ge", "geo", "gf", "gg",
  "ggy", "gh", "gha", "gi", "gib", "gin", "gl", "glp", "gm", "gmb", "gn", "gnb", "gnq", "gp", "gq",
  "gr", "grc", "grd", "grl", "gs", "gt", "gtm", "gu", "guf", "gum", "guy", "gw", "gy", "hk", "hkg",
  "hm", "hn", "hnd", "hr", "hrv", "ht", "hti", "hu", "hun", "id", "idn", "ie", "il", "im", "imn",
  "in", "ind", "io", "iot", "iq", "ir", "ire", "irl", "irn", "irq", "is", "isl", "isr", "it", "ita",
  "jam", "je", "jey", "jm", "jo", "jor", "jp", "jpn", "kaz", "ke", "ken", "kg", "kgz", "kh", "khm",
  "ki", "kir", "km", "kn", "kna", "ko", "kor", "kp", "kr", "kw", "kwt", "ky", "kz", "la", "lao", "lb",
  "lbn", "lbr", "lby", "lc", "lca", "li", "lie", "lk", "lka", "lr", "ls", "lso", "lt", "ltu", "lu",
  "lux", "lv", "lva", "ly", "ma", "mac", "mar", "mc", "mco", "md", "mda", "mdg", "mdv", "me", "mex",
  "mf", "mg", "mh", "mhl", "mk", "mkd", "ml", "mli", "mlt", "mm", "mmr", "mn", "mne", "mng", "mnp",
  "mo", "moz", "mp", "mq", "mr", "mrt", "ms", "msr", "mt", "mtq", "mu", "mus", "mv", "mw", "mwi",
  "mx", "my", "mys", "myt", "mz", "na", "nam", "nc", "ncl", "ne", "ner", "nf", "nfk", "ng", "nga",
  "ni", "nic", "niu", "nl", "nld", "no", "nor", "np", "npl", "nr", "nru", "nu", "nz", "nzl", "om",
  "omn", "oo", "pa", "pak", "pan", "pe", "per", "pf", "pg", "ph", "phl", "pk", "pl", "plw", "pm", "pn",
  "png", "pol", "pr", "pri", "prt", "pry", "ps", "pse", "pt", "pw", "py", "pyf", "qa", "qat", "re",
  "reu", "ro", "rou", "rs", "ru", "rus", "rw", "rwa", "sa", "sau", "sb", "sc", "sd", "sdn", "se",
  "sen", "sg", "sgp", "sh", "si", "sj", "sk", "sl", "slb", "sle", "slv", "sm", "smr", "sn", "so",
  "som", "sr", "srb", "ss", "ssd", "st", "stp", "sur", "sv", "svk", "svn", "swe", "swz", "sx", "sy",
  "syc", "syr", "sz", "tc", "tca", "tcd", "td", "tf", "tg", "tgo", "th", "tha", "tj", "tjk", "tkl",
  "tkm", "tl", "tls", "tm", "tn", "to", "ton", "tr", "tt", "tto", "tun", "tur", "tuv", "tw", "twn",
  "tz", "tza", "ua", "ug", "uga", "uk", "ukr", "un", "ury", "us", "usa", "uy", "uz", "uzb", "va",
  "vat", "vc", "vct", "ve", "ven", "vg", "vgb", "vi", "vir", "vn", "vnm", "vu", "vut", "wf", "wlf",
  "ws", "wsm", "xk", "ye", "yem", "yt", "za", "zaf", "zm", "zmb", "zw", "zwe"
];
